/**
 * SN_SAI_TOL_REG_001 — Tool 관리 (본사) (2026-09-18, 2차 검토 확정)
 *
 * AI 가 고를 수 있는 조회(sai_tool). 조회 ID 는 sys_query 에 있는 것만. 설명(dc_tool)을 보고 AI 가 고른다 — 업무 말로 쓴다.
 * 고칠 때마다 버전이 오르고 이력(sai_toolhst)이 남는다. 외부 전송 차단(yn_block_out)이면 결과 원문이 LLM 회사로 나가지 않는다.
 * [동기화] — 조회 ID(sys_query 메뉴코드_01)가 있는 화면 메뉴와 코드 도움창(팝업)을 후보(초안 · 사용 N)로 넣는다. 있는 것은 건너뛴다. 메뉴가 늘면 버튼 한 번.
 * [승인] [반려] [검토중] [사용] [사용안함] — Ctrl · Shift 클릭으로 고른 줄의 승인상태 · 사용여부를 한 번에 바꾼다. 저장은 툴바 [저장] (2026-10-02, 한 줄씩 콤보를 바꾸던 불편).
 *
 * 조회조건 · 그리드 컬럼 · 폼 · 팝업 정의는 DB(sys_formctrl · sys_formgridcolumn · sys_popup*, 08_AI_화면설정.sql)에서 온다. 조회는 sys_query sn_sai_tol_reg_001_01.
 * 본 : 사용자 등록 SN_SYS_AUT_REG_004 (조회 + 그리드 1)
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import SearchPanel from "@widgets/SearchPanel";
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnButton from "@shared/components/SnButton";
import { menuInfo } from "@shared/type/CommonType";

interface Props {
  Menuprops: menuInfo;
}

const SN_SAI_TOL_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          grid1: rtn.grid.grid1 || [],
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch, onInsert, onDelete, onSave,
  }));

  const onSearch = async () => {
    if (
      gridRefs.current["grid1"].current.getChange().modifiedRowcount > 0
    ) {
      const confirm = await showDialog("confirmCancel", "수정된 내용이 있습니다. 저장하시겠습니까?");
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      /* 다시 읽기 전에 수정 표시를 지운다. 안 지우면 아니오를 눌러도 "수정 중" 으로 남는다 */
      gridRefs.current["grid1"].current?.resetUpdate?.();
      const params = {
        id: "sn_sai_tol_reg_001_01",
        params: searchRef.current.getFormdata(),
      };
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data?.[0] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      gridRefs.current["grid1"].current?.addRow();
    } catch (error: any) {}
  };
  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;
    const confirm = await showDialog("confirm", "선택한 행을 삭제 표시합니다. 저장하면 지워집니다.");
    if (!confirm) return;
    gridRefs.current["grid1"].current?.deleteSelectedRows();
  };
  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      if (!gridRefs.current["grid1"].current.requiredValuesCheck()) return false;
      const sai_toolChanges = gridRefs.current["grid1"].current.getChange();
      if (sai_toolChanges.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }
      const saveData: any = {
        sai_tool: sai_toolChanges,
      };
      const ok = await fnSave("SN_SAI_TOL_REG_001_SAVE_01", saveData);
      if (!ok) return false;
      gridRefs.current["grid1"].current.resetUpdate();
      await onSearch();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current?.resetUpdate(true);
      return false;
    }
  };
  /** 행추가 기본값 — 회사 · 버전 1. 고정값(사용여부 등)은 sys_formgridcolumn.cd_value 가 넣는다 */
  const onBeforeAddTool = (): any => {
    return { cd_firm: userInfo.cd_firm, no_ver: 1 };
  };
  /** 선택한 줄들의 한 칸을 같은 값으로. setDataValue 가 수정 표시를 붙여 툴바 [저장] 이 그대로 받는다 */
  const setSelected = async (field: string, value: string) => {
    const grid = gridRefs.current["grid1"].current;
    grid?.stopEditing();
    let count = 0;
    grid?.forEachNode((node: any) => {
      if (node.isSelected() && node.data?.[field] !== value) {
        node.setDataValue(field, value);
        count++;
      }
    });
    if (count === 0 && !grid?.getSelectedRows()) {
      await showDialog("alter", "먼저 줄을 선택해 주세요. Ctrl · Shift 를 누르고 클릭하면 여러 줄을 고를 수 있습니다.");
    }
  };
  const onImport = async () => {
    if (gridRefs.current["grid1"].current.getChange().modifiedRowcount > 0) {
      await showDialog("alter", "수정 중인 내용을 먼저 저장해 주세요.");
      return;
    }
    const confirm = await showDialog("confirm", "조회 ID 가 있는 화면과 코드 도움창을 Tool 후보(초안 · 사용 안 함)로 동기화합니다. 이미 있는 Tool 은 건드리지 않습니다.");
    if (!confirm) return;
    const result = await callApi("SN_SAI_TOL_REG_001_btnImport", {});
    if (!result?.status) return;
      await onSearch();
  };
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddTool
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel ref={searchRef} data={formData} controls={formControlData} />
      </div>
      <div className="w-full flex h-full min-h-0 space-x-1.5">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "multiple", title: "Tool" }}
            events={grids.grid1.events}
            header={
              <div className="flex space-x-1">
                <SnButton text="승인" onClick={() => { setSelected("tp_status", "APPR"); }} />
                <SnButton text="반려" onClick={() => { setSelected("tp_status", "REJ"); }} />
                <SnButton text="검토중" onClick={() => { setSelected("tp_status", "REVIEW"); }} />
                <SnButton text="사용" onClick={() => { setSelected("yn_use", "Y"); }} />
                <SnButton text="사용안함" onClick={() => { setSelected("yn_use", "N"); }} />
                <SnButton text="동기화" onClick={() => { onImport(); }} />
              </div>
            }
          />
      </div>
    </div>
  );
});

export default SN_SAI_TOL_REG_001;
